import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, IconButton, useTheme, Button, Dialog, DialogTitle, TextField,DialogActions, DialogContent, Paper } from "@mui/material";
import { ref, onValue, remove, push } from "firebase/database";
import { db } from "../../firebase"; // Adjust path based on your Firebase setup
import Notification from "../Notification";
import EditUserModal from "./EditUserModal";
import UserCard from "./UserCard"; // Import the UserCard component
import SearchBar from "../Navigation/SearchBar"; // Import the SearchBar component
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

function UserSettings() {
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null); // Store user data to edit
  const [editModalOpen, setEditModalOpen] = useState(false); // Modal state for editing
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [newUser, setNewUser] = useState({}); // New user state for adding users
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Add user dialog state
  const theme = useTheme();

  // Fetch users from Firebase Realtime Database
  useEffect(() => {
    const usersRef = ref(db, "users");
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedUsers = Object.keys(data).map((key) => ({
          uid: key,
          ...data[key],
        }));
        setUsers(formattedUsers);
      }
    });
  }, []);

  const handleDeleteUser = async (user) => {
    const result = await Notification.confirm({
      theme,
      title: `Delete User: ${user.Identifier}`,
      text: `Are you sure you want to delete user ${user.Identifier} (${user.Email})? This action cannot be undone.`,
      icon: "warning",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await remove(ref(db, `users/${user.uid}`));
        Notification.success(theme, "Success", "User deleted successfully.");
      } catch (error) {
        console.error("Error deleting user:", error);
        Notification.error(
          theme,
          "Error",
          "There was a problem deleting the user.",
        );
      }
    }
  };

  const handleEditUser = (user) => {
    setEditUser(user); // Set the selected user to edit
    setEditModalOpen(true); // Open the edit modal
  };

  const handleEditModalClose = () => {
    setEditUser(null); // Clear the selected user
    setEditModalOpen(false); // Close the modal
  };

  // Handle adding a new user (open modal or form)
  const handleDialogOpen = () => setIsDialogOpen(true);
  const handleDialogClose = () => setIsDialogOpen(false);

  const handleAddUser = () => {
    // Add new user logic (push to Firebase)
    if (newUser.Identifier && newUser.Email) {
      push(ref(db, "users"), newUser);
      Notification.success(theme, "Success", "New user added successfully.");
      setNewUser({}); // Clear the form
      handleDialogClose();
    }
  };

  return (
    <Paper elevation={3} sx={{ backgroundColor: theme.palette.background.default, padding: theme.spacing(2) }}>
      {/* Title and SearchBar with Add button */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap" // Ensure it wraps on smaller screens
        sx={{ p:1,  backgroundColor: theme.palette.success.main, mb: theme.spacing(2) }}
      >
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          color={theme.palette.success.text}
        >
          User Management
        </Typography>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box width={500}>
            <SearchBar
              searchTerm={searchTerm}
              onChange={setSearchTerm}
              placeholderText="Search Users"
              forceExpanded={true} // Force the search bar to stay expanded
            />
          </Box>
          <IconButton onClick={handleDialogOpen} color="primary">
            <FontAwesomeIcon icon={faPlus} />
          </IconButton>
        </Box>
      </Box>

      {/* User Grid */}
      <Grid container spacing={3}>
        {users
          .filter(
            (user) =>
              user.Identifier.toLowerCase().includes(searchTerm.toLowerCase()) || // Filter by identifier
              user.Email.toLowerCase().includes(searchTerm.toLowerCase()) // Filter by email
          )
          .map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.uid}>
              <UserCard user={user} onEdit={handleEditUser} onDelete={handleDeleteUser} />
            </Grid>
          ))}
      </Grid>

      {/* Edit User Modal */}
      <EditUserModal
        open={editModalOpen}
        user={editUser}
        onClose={handleEditModalClose}
        onSave={() => {
          setEditModalOpen(false);
          setEditUser(null);
        }}
      />

      {/* Dialog for Adding New User */}
      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            label="User Identifier"
            variant="outlined"
            fullWidth
            value={newUser.Identifier || ""}
            onChange={(e) => setNewUser({ ...newUser, Identifier: e.target.value })}
            sx={{ marginBottom: theme.spacing(2) }}
          />

          <TextField
            label="User Email"
            variant="outlined"
            fullWidth
            value={newUser.Email || ""}
            onChange={(e) => setNewUser({ ...newUser, Email: e.target.value })}
            sx={{ marginBottom: theme.spacing(2) }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default UserSettings;
